<template>
    <Modal :cancel="cancel"
           class="refundable-payments-modal"
           cancel-label="CLOSE"
           icon="fa-money-bill-transfer"
           title="REFUNDABLE_PAYMENTS.MODAL_TITLE">
        <table class="table table-striped table-hover">
            <thead>
                <tr>
                    <th>
                        {{ l('REFUNDABLE_PAYMENTS.PAYMENT_DETAILS') }}
                    </th>
                    <th class="text-right">
                        {{ l('REFUNDABLE_PAYMENTS.AMOUNT') }}
                    </th>
                    <th class="text-right">
                        {{ l('REFUNDABLE_PAYMENTS.REFUNDABLE_AMOUNT') }}
                    </th>
                    <th></th>
                </tr>
            </thead>
            <tbody>
                <tr v-for="payment in payments"
                    :key="payment.ID">
                    <td>
                        <span>{{ payment.paymentMethod.shortName }}</span>

                        <small v-if="payment.takenAt"
                               class="block">
                            {{ dateFilter(payment.takenAt, true) }}
                        </small>
                        <small v-else-if="payment.confirmedAt"
                               class="block">
                            {{ dateFilter(payment.confirmedAt, true) }}
                        </small>
                    </td>
                    <td class="text-right">
                        {{ currencyFilter(payment.amount) }}
                    </td>
                    <td class="text-right">
                        {{ currencyFilter(availableRefundAmount(payment)) }}
                    </td>
                    <td class="text-right">
                        <button v-if="availableRefundAmount(payment) !== 0"
                                class="btn btn-primary"
                                @click="selectPayment(payment)">
                            {{ l('REFUNDABLE_PAYMENTS.REFUND') }}
                            {{ currencyFilter(Math.abs(maxRefundAmount(payment))) }}
                        </button>
                    </td>
                </tr>
            </tbody>
        </table>
    </Modal>
</template>

<script>
import DateFormat from '@/lib/Dates/DateFormat';
import Modal from '@/vue/components/Modals/Modal';
import RefundData from '@/lib/PaymentMethods/Clover/RefundData';
import {
    getPaymentStatusLangKey,
} from '@/lib/Payments/paymentHelperFunctions';
import { ModalPropsMixin } from '@/vue/mixins/ModalPropsMixin';

export default {
    name: 'RefundablePaymentsModal',

    components: { Modal },

    mixins: [
        ModalPropsMixin,
    ],

    props: {
        payments: {
            type: Array,
            required: true,
        },
        refundPayments: {
            type: Array,
            required: true,
        },
        refundNeeded: {
            type: Number,
            required: true,
        },
    },

    data() {
        return {
            DateFormat,
            refundPaymentsByOriginalPaymentID: {},
        };
    },

    created() {
        if (this.refundPayments.length) {
            this.refundPayments.forEach((refundPayment) => {
                if (!this.refundPaymentsByOriginalPaymentID.hasOwnProperty(refundPayment.originalPaymentID)) {
                    this.refundPaymentsByOriginalPaymentID[refundPayment.originalPaymentID] = 0;
                }
                this.refundPaymentsByOriginalPaymentID[refundPayment.originalPaymentID] += refundPayment.amount;
            });
        }
    },

    methods: {
        /**
         * These statuses correspond to those used in Backoffice except for cancelled. When a Payment on Account payment
         * is fulfilled by a real payment we may want to update it to paid = 0 and pending = 0. This is a temporary
         * solution as our payment statuses are a bit simplistic at the moment.
         *
         * @param {Payment} payment
         * @returns {string}
         */
        displayStatus(payment) {
            return getPaymentStatusLangKey(payment);
        },

        /**
         * Returns a -ve refund amount.
         * refundNeeded is -ve and availableRefundAmount() is +ve so pick the smaller (closer to zero) amount.
         *
         * @param {Payment} payment
         *
         * @return {Number}
         */
        selectPayment(payment) {
            return this.resolve(new RefundData(payment, this.maxRefundAmount(payment)));
        },

        /**
         * The non refunded balance of an existing payment. This may be more than the required refund amount.
         *
         * @param {Payment} payment
         *
         * @return {Number}
         */
        availableRefundAmount(payment) {
            if (this.refundPaymentsByOriginalPaymentID[payment.ID]) {
                // payment.outstandingRefundableAmount is +ve
                // this.refundPaymentsByOriginalPaymentID[payment.ID] is -ve
                return payment.outstandingRefundableAmount + this.refundPaymentsByOriginalPaymentID[payment.ID];
            }
            return payment.outstandingRefundableAmount;
        },

        /**
         * The maximum amount that can be refunded for a payment. This may be less than the required refund amount.
         *
         * @param {Payment} payment
         *
         * @return {Number}
         */
        maxRefundAmount(payment) {
            return Math.max(this.refundNeeded, (this.availableRefundAmount(payment) * -1));
        },
    },
};
</script>
